import { useEffect, useState } from 'react';
import { MenuTop } from '../../../components/menu/top/menu-top';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper,
    CircularProgress,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Chip,
    Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import InputComponent from '../../../components/input/input';
import '../styles/admin-page.css';
import '../styles/list-dd-curto.css';
import { deleteDDCurtoAnswer, getAllDDCurtoAnswer, IDDCurtoTestData } from '../../../features/search/service/search-service';
import IconButtonComponent from '../../../components/Icon/icon-button';

export const AdminDDCurtoTestsPage = () => {
    const [tests, setTests] = useState<IDDCurtoTestData[]>([]);
    const [filteredTests, setFilteredTests] = useState<IDDCurtoTestData[]>([]);
    const [loading, setLoading] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedTest, setSelectedTest] = useState<IDDCurtoTestData | null>(null);
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');

    useEffect(() => {
        fetchAllTests();
    }, []);

    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredTests(tests);
            return;
        }

        const term = searchTerm.toLowerCase().trim();
        const filtered = tests.filter(test => {
            const nome = (test.nome || '').toLowerCase();
            const recordId = (test.record_id || '').toLowerCase();
            const companyName = (test.companyName || '').toLowerCase();

            return nome.includes(term) || 
                   recordId.includes(term) || 
                   companyName.includes(term);
        });

        setFilteredTests(filtered);
    }, [searchTerm, tests]);

    const fetchAllTests = async () => {
        try {
            setLoading(true);
            const response = await getAllDDCurtoAnswer();
            setTests(response.data);
            setFilteredTests(response.data);
        } catch (error) {
            console.error('Erro ao carregar testes:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
    };

    const handleDeleteClick = (test: IDDCurtoTestData) => {
        setSelectedTest(test);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedTest) return;

        try {
            await deleteDDCurtoAnswer(selectedTest.id);
            setSuccessMessage(`Teste "${selectedTest.nome}" excluído com sucesso!`);
            setDeleteDialogOpen(false);
            setSelectedTest(null);
            
            fetchAllTests();
            
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (error) {
            console.error('Erro ao excluir teste:', error);
            alert('Erro ao excluir teste. Tente novamente.');
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setSelectedTest(null);
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatTime = (seconds: number) => {
        if (!seconds) return '0m 0s';
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
    };

    return (
        <div>
            <MenuTop />
            
            {/* Chip Fixo no Canto Direito */}
            <Chip 
                label={`Total: ${filteredTests.length}`}
                className="admin-tests-chip-fixed"
                sx={{ 
                    backgroundColor: '#3a8d8e', 
                    color: 'white',
                    fontWeight: 500,
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    padding: '20px 12px'
                }}
            />

            <div className="admin-page-container admin-tests-container">
                {/* Header Clean */}
                <div className="admin-tests-header">
                    <h1 className="admin-tests-title">
                        Gerenciar Testes Aplicados
                    </h1>

                    {/* Input Maior e Centralizado */}
                    <div className="admin-tests-search-container">
                        <div className="admin-tests-search-wrapper">
                            <InputComponent
                                id="search-tests"
                                label="Buscar por nome, record ID ou empresa"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                type="text"
                            />
                        </div>
                        {searchTerm && (
                            <IconButton
                                size="small"
                                onClick={handleClearSearch}
                                sx={{ 
                                    color: '#999',
                                    '&:hover': { 
                                        backgroundColor: '#f5f5f5',
                                        color: '#666'
                                    }
                                }}
                                title="Limpar busca"
                            >
                                <ClearIcon />
                            </IconButton>
                        )}
                    </div>
                </div>

                {successMessage && (
                    <Alert 
                        severity="success" 
                        onClose={() => setSuccessMessage('')}
                        className="admin-tests-alert"
                    >
                        {successMessage}
                    </Alert>
                )}

                {loading ? (
                    <div className="admin-tests-loading">
                        <div className="admin-tests-loading-spinner">
                            <CircularProgress 
                                size={48} 
                                thickness={3.5}
                                sx={{ color: '#3a8d8e' }} 
                            />
                        </div>
                        <Typography className="admin-tests-loading-text">
                            Carregando testes...
                        </Typography>
                        <Typography className="admin-tests-loading-subtext">
                            Aguarde um momento
                        </Typography>
                    </div>
                ) : (
                    <>
                        {searchTerm && filteredTests.length === 0 && (
                            <Alert severity="info" className="admin-tests-alert">
                                Nenhum teste encontrado para "<strong>{searchTerm}</strong>"
                            </Alert>
                        )}

                        {!searchTerm && filteredTests.length === 0 && (
                            <div className="admin-tests-empty-state">
                                <HourglassEmptyIcon className="admin-tests-empty-state-icon" />
                                <Typography className="admin-tests-empty-state-text">
                                    Nenhum teste encontrado
                                </Typography>
                                <Typography className="admin-tests-empty-state-subtext">
                                    Os testes aplicados aparecerão aqui
                                </Typography>
                            </div>
                        )}

                        {filteredTests.length > 0 && (
                            <TableContainer 
                                component={Paper} 
                                className="admin-tests-table-container"
                                sx={{ 
                                    maxHeight: 'calc(100vh - 300px)',
                                    overflow: 'auto'
                                }}
                            >
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell className="admin-tests-table-cell-header">
                                                Nome
                                            </TableCell>
                                            <TableCell className="admin-tests-table-cell-header">
                                                Record ID
                                            </TableCell>
                                            <TableCell className="admin-tests-table-cell-header">
                                                Empresa
                                            </TableCell>
                                            <TableCell className="admin-tests-table-cell-header">
                                                Criado em
                                            </TableCell>
                                            <TableCell className="admin-tests-table-cell-header">
                                                Tempo
                                            </TableCell>
                                            <TableCell className="admin-tests-table-cell-header">
                                                ED50
                                            </TableCell>
                                            <TableCell className="admin-tests-table-cell-header">
                                                K
                                            </TableCell>
                                            <TableCell 
                                                align="center"
                                                className="admin-tests-table-cell-header"
                                                sx={{ width: '70px' }}
                                            >
                                                Ações
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredTests.map((test) => (
                                            <TableRow 
                                                key={test.id}
                                                className="admin-tests-table-row"
                                            >
                                                <TableCell className="admin-tests-table-cell-name" title={test.nome || 'N/A'}>
                                                    {test.nome || 'N/A'}
                                                </TableCell>
                                                <TableCell className="admin-tests-table-cell-body">
                                                    {test.record_id || 'N/A'}
                                                </TableCell>
                                                <TableCell className="admin-tests-table-cell-chip">
                                                    <Chip 
                                                        label={test.companyName || 'N/A'}
                                                        size="small"
                                                        sx={{ 
                                                            backgroundColor: '#e8f5e9',
                                                            color: '#2e7d32',
                                                            height: '22px',
                                                            fontSize: '11px',
                                                            fontFamily: 'Roboto, sans-serif',
                                                            fontWeight: 500
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell className="admin-tests-table-cell-body">
                                                    {formatDate(test.createdAt || '')}
                                                </TableCell>
                                                <TableCell className="admin-tests-table-cell-body">
                                                    {formatTime(test.totalTimeSeconds || 0)}
                                                </TableCell>
                                                <TableCell className="admin-tests-table-cell-body">
                                                    {test.ed50 ? parseFloat(test.ed50).toFixed(2) : 'N/A'}
                                                </TableCell>
                                                <TableCell className="admin-tests-table-cell-body">
                                                    {test.k ? parseFloat(test.k).toFixed(4) : 'N/A'}
                                                </TableCell>
                                                <TableCell align="center" className="admin-tests-table-cell-actions">
                                                    <IconButtonComponent 
                                                        label=""
                                                        icon={<DeleteIcon />}
                                                        onClick={() => handleDeleteClick(test)}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                )}
            </div>

            {/* Dialog de Confirmação */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ 
                    backgroundColor: '#f44336', 
                    color: 'white', 
                    padding: '16px 24px',
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 500,
                    fontSize: '18px'
                }}>
                    Confirmar Exclusão
                </DialogTitle>
                <DialogContent sx={{ padding: '24px' }}>
                    <Typography 
                        sx={{ 
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: '14px',
                            color: '#666',
                            marginBottom: '16px'
                        }}
                    >
                        Tem certeza que deseja excluir este teste?
                    </Typography>
                    {selectedTest && (
                        <div className="admin-tests-dialog-info">
                            <Typography variant="body2" className="admin-tests-dialog-info-text">
                                <strong>Nome:</strong> {selectedTest.nome}
                            </Typography>
                            <Typography variant="body2" className="admin-tests-dialog-info-text">
                                <strong>Record ID:</strong> {selectedTest.record_id}
                            </Typography>
                            <Typography variant="body2" className="admin-tests-dialog-info-text">
                                <strong>Empresa:</strong> {selectedTest.companyName}
                            </Typography>
                            <Typography variant="body2" className="admin-tests-dialog-info-text">
                                <strong>Criado em:</strong> {formatDate(selectedTest.createdAt || '')}
                            </Typography>
                        </div>
                    )}
                    <Typography variant="body2" className="admin-tests-dialog-warning">
                        ⚠️ Esta ação não pode ser desfeita
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px' }}>
                    <Button 
                        onClick={handleDeleteCancel}
                        sx={{ 
                            color: '#666',
                            fontFamily: 'Roboto, sans-serif',
                            textTransform: 'none',
                            fontSize: '14px'
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        sx={{ 
                            backgroundColor: '#f44336',
                            fontFamily: 'Roboto, sans-serif',
                            textTransform: 'none',
                            fontSize: '14px',
                            '&:hover': { backgroundColor: '#d32f2f' }
                        }}
                    >
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};